import { useCallback, useEffect, useState } from "react"
import { useMount, useToggle, useUnmount } from "react-use"
import * as VW from "voximplant-websdk"

import { Analytics } from "utils/analytics"
import { useInputDevicesContext } from "modules/Call/InputDevices"
import { VoximplantLocalVideo } from "modules/Call/VoximplantLocalVideo"
import { Call } from "modules/Call/sdk"
import * as speakerIndicator from "modules/Call/common/utils/speakerIndicator"
import { browser } from "utils"
import { SentryWrapper } from "utils/ThirdParty/Sentry/SentryWrapper"

function sendCameraAnalytics(isInCall: boolean, on: boolean) {
    if (isInCall) {
        if (on) {
            Analytics.call_camera_on()
        } else {
            Analytics.call_camera_off()
        }
    } else {
        if (on) {
            Analytics.lobby_camera_on()
        } else {
            Analytics.lobby_camera_off()
        }
    }
}

function sendMicrophoneAnalytics(isInCall: boolean, on: boolean) {
    if (isInCall) {
        if (on) {
            Analytics.call_mic_on()
        } else {
            Analytics.call_mic_off()
        }
    } else {
        if (on) {
            Analytics.lobby_mic_on()
        } else {
            Analytics.lobby_mic_off()
        }
    }
}

export interface ControlsValue {
    videoLoading: boolean
    sendVideoError?: Error
    screenShareSupported: boolean
    screenShareEnabled: boolean
    showScreenShareLoader: boolean
}

export interface ControlsHandlers {
    setCamera(id: number | string): Promise<void>

    setMicrophone(id: number | string): Promise<void>

    toggleMicrophone(): void

    toggleCamera(): Promise<void>

    runToggleScreenShare(): Promise<void>
}

export function useControls(call?: Call): [ControlsValue, ControlsHandlers] {
    const [sendVideoError, setSendVideoError] = useState<Error>()
    const [videoLoading, setVideoLoading] = useToggle(false)

    const [screenShareSupported, setScreenShareSupported] = useState(false)
    const [screenShareEnabled, setScreenShareEnabled] = useState(false)
    const [showScreenShareLoader, setShowScreenShareLoader] = useState(false)

    const [
        {
            cameraAccessed,
            microphoneAccessed,
            cameraEnabled,
            microphoneEnabled,
            camera,
            microphone,
        },
        { selectCamera, selectMicrophone, enableCamera, enableMicrophone },
    ] = useInputDevicesContext()

    const setMicrophone = useCallback(
        async (inputId: number | string) => {
            const sdkManager = VW.Hardware.AudioDeviceManager.get()
            const outputId = sdkManager.getDefaultAudioSettings().outputId
            const params = {
                inputId: inputId.toString(),
            }
            if (outputId) {
                Object.assign(params, { outputId })
            }
            if (call) {
                await sdkManager.setCallAudioSettings(call, params)
                selectMicrophone(inputId)
                Analytics.call_mic_change_selected()
            } else {
                selectMicrophone(inputId)
                Analytics.lobby_mic_change_selected()
            }
            sdkManager.setDefaultAudioSettings(params)
        },
        [call]
    )

    const setCamera = useCallback(
        async (cameraId: number | string) => {
            const sdkManager = VW.Hardware.CameraManager.get()

            function setVideoSettings() {
                return call
                    ? sdkManager.setCallVideoSettings(call, {
                          cameraId: cameraId.toString(),
                      })
                    : sdkManager.setDefaultVideoSettings({ cameraId: cameraId.toString() })
            }

            await setVideoSettings()
            selectCamera(cameraId)
            if (call) {
                Analytics.call_camera_change_selected()
            } else {
                Analytics.lobby_camera_change_selected()
            }
        },
        [call]
    )

    function toggleMicrophone() {
        if (!microphoneAccessed) return
        const newValue = !microphoneEnabled
        newValue ? call?.unmuteMicrophone() : call?.muteMicrophone()
        enableMicrophone(newValue)
        speakerIndicator.clearOutbound()
        sendMicrophoneAnalytics(!!call, newValue)
    }

    async function toggleCamera(force?: boolean) {
        if (!cameraAccessed || videoLoading) return
        const newValue = force !== undefined ? force : !cameraEnabled
        if (screenShareEnabled) {
            enableCamera(newValue)
            return
        }
        setVideoLoading(true)
        try {
            if (call) {
                if (newValue) {
                    enableCamera(await VoximplantLocalVideo.getInstance().enable(newValue))
                    const event = await call.sendVideo(newValue)
                    enableCamera(await VoximplantLocalVideo.getInstance().enable(event.result))
                } else {
                    const event = await call.sendVideo(newValue)
                    if (event.result) {
                        enableCamera(await VoximplantLocalVideo.getInstance().enable(newValue))
                    }
                }
            } else {
                enableCamera(await VoximplantLocalVideo.getInstance().enable(newValue))
            }
            sendCameraAnalytics(!!call, newValue)
        } catch (err) {
            setSendVideoError(
                err instanceof Error ? err : new Error("Send video error", { cause: err })
            )
        } finally {
            setVideoLoading(false)
        }
    }

    async function initCamera() {
        if (camera?.id !== undefined) {
            await setCamera(camera.id)
        }
        if (cameraEnabled) {
            await VoximplantLocalVideo.getInstance().enable(true)
        }
    }

    async function initMicrophone() {
        if (microphone?.id === undefined) return
        await setMicrophone(microphone.id)
    }

    async function runToggleScreenShare() {
        if (!(call && screenShareSupported) || showScreenShareLoader) {
            return
        }
        setShowScreenShareLoader(true)
        if (screenShareEnabled) {
            try {
                void call.stopSharingScreen() // TODO: replace void with await when voximplant bug will be fixed
                setScreenShareEnabled(false)
                enableCamera(false)
            } finally {
                setShowScreenShareLoader(false)
            }
        } else {
            try {
                await call.sendVideo(true)
                await VoximplantLocalVideo.getInstance().enable(true)
                enableCamera(true)

                const event = await call.shareScreen()

                if (event.result) {
                    setScreenShareEnabled(true)
                } else {
                    SentryWrapper.getInstance().captureException(
                        `Failed to start screen sharing: ${event.result}`
                    )
                }
            } catch (exception) {
                SentryWrapper.getInstance().captureException(exception)
                if (!(exception instanceof DOMException && exception.name === "NotAllowedError")) {
                    throw exception
                }
            } finally {
                setShowScreenShareLoader(false)
            }
        }
    }

    useEffect(() => {
        if (!cameraAccessed) return
        void initCamera()
    }, [cameraAccessed])

    useEffect(() => {
        if (!microphoneAccessed) return
        void initMicrophone()
    }, [microphoneAccessed])

    useEffect(() => {
        if (!(cameraAccessed && camera)) return
        void setCamera(camera.id)
    }, [camera])

    useEffect(() => {
        if (!(microphoneAccessed && microphone)) return
        void setMicrophone(microphone.id)
    }, [microphone])

    useEffect(() => {
        if (sendVideoError) {
            console.error(sendVideoError)
            SentryWrapper.getInstance().captureException(sendVideoError, {})
        }
    }, [sendVideoError])

    useEffect(() => {
        if (!call) return
        call.on(VW.CallEvents.SharingStopped, () => {
            setScreenShareEnabled(false)
            void call.sendVideo(false)
            void VoximplantLocalVideo.getInstance().enable(false)
            enableCamera(false)
        })
    }, [call])

    useMount(() => {
        VW.getInstance()
            .screenSharingSupported()
            .then((isSupported) => {
                setScreenShareSupported(browser.platform.type === "desktop" && isSupported)
            })
            .catch(() => {
                setScreenShareSupported(false)
            })
    })

    useUnmount(() => {
        void VoximplantLocalVideo.getInstance().enable(false)
    })

    return [
        {
            videoLoading,
            sendVideoError,
            screenShareSupported,
            screenShareEnabled,
            showScreenShareLoader,
        },
        {
            setCamera,
            setMicrophone,
            toggleMicrophone,
            toggleCamera,
            runToggleScreenShare,
        },
    ]
}
